import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDrop } from "react-dnd";
import { CanvasContext } from "../../../contexts/CanvasContext";
import ButtonComponent from "./ButtonComponent";
import ImageComponent from "./ImageComponent";
import HeaderComponent from "./HeaderComponent";
import ParagraphComponent from "./ParagraphComponent";
import IconComponent from "./IconComponent";
import LinkComponent from "./LinkComponent";
import CheckboxComponent from "./CheckboxComponent";
import RadioComponent from "./RadioComponent";
import ListComponent from "./ListComponent";
import TableComponent from "./TableComponent";
import VideoComponent from "./VideoComponent";
import TextInputComponent from "./TextComponent";
import RangeComponent from "./RangeComponent";
import PasswordInputComponent from "./PasswordInputComponent";
import EmailInputComponent from "./EmailInputComponent";
import NumberInputComponent from "./NumberInputComponent";
import DateInputComponent from "./DateInputComponent";
import ColorInputComponent from "./ColorInputComponent";
import FileInputComponent from "./FileInputComponent";
import AudioComponent from "./AudioComponent";
import DropDownComponent from "./DropDownComponent";
import MapComponent from "./MapComponent";
import Division from "./Division";
import Nav from "./Nav";
import HeaderTag from "./HeaderTag";
import Hgroup from "./Hgroup";
import Footer from "./Footer";
import Span from "./Span";
import FormComponent from "./FormComponent";
import MainComponent from "./MainComponent";
import ArticleComponent from "./ArticleComponent";
import AsideComponent from "./AsideComponent";
import BRTag from "./BRTag";
import HRTag from "./HRTag";
import OutputComponent from "./OutputComponent";
import IframeComponent from "./IframeComponent";

const Section = ({ section }) => {
  const sectionRef = useRef(null);

  const { addElement, hoveredDivision, files, addCanvasDrop, addDivision } =
    useContext(CanvasContext);
  const isHovered = section.id === hoveredDivision;
  const [isSectionHovered, setIsSectionHovered] = useState(false);
  const [isSectionFocused, setIsSectionFocused] = useState(false);
  const [isSectionActive, setIsSectionActive] = useState(false);
  const [backgroundImageUrl, setBackgroundImageUrl] = useState(null);

  const [, drop] = useDrop({
    accept: [
      "DroppableDiv",
      "DroppableButton",
      "DroppableImage",
      "DroppableHeading",
      "DroppableParagraph",
      "DroppableIcon",
      "DroppableLink",
      "DroppableCheckbox",
      "DroppableRadio",
      "DroppableList",
      "DroppableTable",
      "DroppableVideo",
      "DroppableAudio",
      "DroppableTextInput",
      "DroppableRangeInput",
      "DroppablePasswordInput",
      "DroppableEmailInput",
      "DroppableNumberInput",
      "DroppableDateInput",
      "DroppableColorInput",
      "DroppableFileInput",
      "DroppableDropDown",
      "DroppableMap",
      "DroppableNav",
      "DroppableHeader",
      "DroppableHGroup",
      "DroppableFooter",
      "DroppableSection",
      "DroppableSpan",
      "DroppableForm",
      "DroppableMain",
      "DroppableArticle",
      "DroppableAside",
      "DroppableHorizontalRule",
      "DroppableLineBreak",
      "DroppableIframe",

    ],
    drop: (item, monitor) => {
      if (!monitor.didDrop()) {
        if (item.type === "DroppableDiv") {
          addDivision(section.id, item.name, item.type);
        } else if (
          item.type === "DroppableNav" ||
          item.type === "DroppableHeader" ||
          item.type === "DroppableHGroup" ||
          item.type === "DroppableFooter" ||
          item.type === "DroppableSection" ||
          item.type === "DroppableSpan" ||
          item.type === "DroppableForm" ||
          item.type === "DroppableMain" ||
          item.type === "DroppableArticle" ||
          item.type === "DroppableAside"
        ) {
          addCanvasDrop(section.id, item.name, item.type);
        } else {
          addElement(section.id, item.name, item.type);
        }
      }
    },
  });

  const constructGradientString = () => {
    const {
      backgroundGradientImage,
      gradientDirection,
      gradientShape,
      gradientPosition,
      startColor,
      endColor,
      gradientAngle,
      patternLength,
    } = section;

    switch (backgroundGradientImage) {
      case "linear-gradient":
        return `linear-gradient(${gradientDirection || "to right"}, ${
          startColor || "#ff7e5f"
        }, ${endColor || "#feb47b"})`;

      case "radial-gradient":
        return `radial-gradient(${gradientShape || "circle"} at ${
          gradientPosition || "center"
        }, ${startColor || "#ff7e5f"}, ${endColor || "#feb47b"})`;

      case "repeating-linear-gradient":
        return `repeating-linear-gradient(${gradientAngle || "45"}deg, ${
          startColor || "#606dbc"
        }, ${endColor || "#465298"} ${patternLength || "10"}px)`;

      case "repeating-radial-gradient":
        return `repeating-radial-gradient(${gradientShape || "circle"} at ${
          gradientPosition || "center"
        }, ${startColor || "#ff7e5f"}, ${endColor || "#feb47b"} ${
          patternLength || "10"
        }%)`;

      default:
        return "none";
    }
  };
  const constructHoverGradientString = () => {
    const {
      hoveredbackgroundGradientImage,
      hoveredgradientDirection,
      hoveredgradientShape,
      hoveredgradientPosition,
      hoveredstartColor,
      hoveredendColor,
      hoveredgradientAngle,
      hoveredpatternLength,
    } = section;

    switch (hoveredbackgroundGradientImage) {
      case "linear-gradient":
        return `linear-gradient(${hoveredgradientDirection || "to right"}, ${
          hoveredstartColor || "#ff7e5f"
        }, ${hoveredendColor || "#feb47b"})`;

      case "radial-gradient":
        return `radial-gradient(${hoveredgradientShape || "circle"} at ${
          hoveredgradientPosition || "center"
        }, ${hoveredstartColor || "#ff7e5f"}, ${hoveredendColor || "#feb47b"})`;

      case "repeating-linear-gradient":
        return `repeating-linear-gradient(${hoveredgradientAngle || "45"}deg, ${
          hoveredstartColor || "#606dbc"
        }, ${hoveredendColor || "#465298"} ${hoveredpatternLength || "10"}px)`;

      case "repeating-radial-gradient":
        return `repeating-radial-gradient(${
          hoveredgradientShape || "circle"
        } at ${hoveredgradientPosition || "center"}, ${
          hoveredstartColor || "#ff7e5f"
        }, ${hoveredendColor || "#feb47b"} ${hoveredpatternLength || "10"}%)`;

      default:
        return "none";
    }
  };
  const constructFocusedGradientString = () => {
    const {
      focusedbackgroundGradientImage,
      focusedgradientDirection,
      focusedgradientShape,
      focusedgradientPosition,
      focusedstartColor,
      focusedendColor,
      focusedgradientAngle,
      focusedpatternLength,
    } = section;

    switch (focusedbackgroundGradientImage) {
      case "linear-gradient":
        return `linear-gradient(${focusedgradientDirection || "to right"}, ${
          focusedstartColor || "#ff7e5f"
        }, ${focusedendColor || "#feb47b"})`;

      case "radial-gradient":
        return `radial-gradient(${focusedgradientShape || "circle"} at ${
          focusedgradientPosition || "center"
        }, ${focusedstartColor || "#ff7e5f"}, ${focusedendColor || "#feb47b"})`;

      case "repeating-linear-gradient":
        return `repeating-linear-gradient(${focusedgradientAngle || "45"}deg, ${
          focusedstartColor || "#606dbc"
        }, ${focusedendColor || "#465298"} ${focusedpatternLength || "10"}px)`;

      case "repeating-radial-gradient":
        return `repeating-radial-gradient(${
          focusedgradientShape || "circle"
        } at ${focusedgradientPosition || "center"}, ${
          focusedstartColor || "#ff7e5f"
        }, ${focusedendColor || "#feb47b"} ${focusedpatternLength || "10"}%)`;

      default:
        return "none";
    }
  };
  const constructActivatedGradientString = () => {
    const {
      activatedbackgroundGradientImage,
      activatedgradientDirection,
      activatedgradientShape,
      activatedgradientPosition,
      activatedstartColor,
      activatedendColor,
      activatedgradientAngle,
      activatedpatternLength,
    } = section;

    switch (activatedbackgroundGradientImage) {
      case "linear-gradient":
        return `linear-gradient(${activatedgradientDirection || "to right"}, ${
          activatedstartColor || "#ff7e5f"
        }, ${activatedendColor || "#feb47b"})`;

      case "radial-gradient":
        return `radial-gradient(${activatedgradientShape || "circle"} at ${
          activatedgradientPosition || "center"
        }, ${activatedstartColor || "#ff7e5f"}, ${
          activatedendColor || "#feb47b"
        })`;

      case "repeating-linear-gradient":
        return `repeating-linear-gradient(${
          activatedgradientAngle || "45"
        }deg, ${activatedstartColor || "#606dbc"}, ${
          activatedendColor || "#465298"
        } ${activatedpatternLength || "10"}px)`;

      case "repeating-radial-gradient":
        return `repeating-radial-gradient(${
          activatedgradientShape || "circle"
        } at ${activatedgradientPosition || "center"}, ${
          activatedstartColor || "#ff7e5f"
        }, ${activatedendColor || "#feb47b"} ${
          activatedpatternLength || "10"
        }%)`;

      default:
        return "none";
    }
  };
  const generateKeyframes = () => {
    if (!section.keyframes || section.keyframes.length === 0) return null;

    return section.keyframes.map((kf) => {
      const properties = {};
      let transform = "";

      Object.entries(kf.properties).forEach(([key, value]) => {
        // Remove 'KEY' prefix if present
        let processedKey = key.startsWith("KEY") ? key.slice(3) : key;

        // Handle transform properties
        if (
          processedKey === "scale" ||
          processedKey === "translateX" ||
          processedKey === "translateY" ||
          processedKey === "rotate" ||
          processedKey === "skewX" ||
          processedKey === "skewY"
        ) {
          if (value) {
            switch (processedKey) {
              case "scale":
                transform += `scale(${value}) `;
                break;
              case "translateX":
                transform += `translateX(${value}) `;
                break;
              case "translateY":
                transform += `translateY(${value}) `;
                break;
              case "rotate":
                transform += `rotate(${value}) `;
                break;
              case "skewX":
                transform += `skewX(${value}) `;
                break;
              case "skewY":
                transform += `skewY(${value}) `;
                break;
              default:
                break;
            }
          }
        } else {
          // Convert camelCase to kebab-case for other CSS properties
          properties[processedKey] = value;
        }
      });

      // Add transform property if it was built
      if (transform) {
        properties["transform"] = transform.trim();
      }

      return {
        offset: kf.position / 100,
        ...properties,
      };
    });
  };

  useEffect(() => {
    if (
      sectionRef.current &&
      section.keyframes &&
      section.keyframes.length > 0
    ) {
      const keyframes = generateKeyframes();
      const animation = sectionRef.current.animate(keyframes, {
        duration: parseFloat(section.animationDuration) * 1000 || 1000,
        delay: parseFloat(section.animationDelay) * 1000 || 0,
        iterations:
          section.animationIterationCount === "infinite"
            ? Infinity
            : parseFloat(section.animationIterationCount) || 1,
        direction: section.animationDirection || "normal",
        fill: section.animationFillMode || "none",
        easing: section.animationTimingFunction || "linear",
      });

      return () => animation.cancel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    section.keyframes,
    section.animationDuration,
    section.animationDelay,
    section.animationIterationCount,
    section.animationDirection,
    section.animationFillMode,
    section.animationTimingFunction,
  ]);

  useEffect(() => {
    const fetchBackgroundImage = async () => {
      if (section.backgroundImage && section.backgroundImage.startsWith("./")) {
        const imageName = section.backgroundImage.replace("./", "");
        const file = files.find((f) => f.name === imageName);
        if (file && file.data) {
          setBackgroundImageUrl(file.data);
        } else {
          console.error("Image not found in files:", imageName);
        }
      } else {
        setBackgroundImageUrl(section.backgroundImage);
      }
    };

    fetchBackgroundImage();
  }, [section.backgroundImage, files]);

 
  const focusedStyles =
    isSectionFocused && section.pseudoClass === "focus"
      ? {
          width: section.focusedwidth || section.width || "100%",
          height: section.focusedheight || section.height || "auto",

          padding: section.focusedpadding || section.padding || "8px",
          paddingTop: section.focusedpaddingTop || section.focusedpadding || "8px",
          paddingRight: section.focusedpaddingRight || section.focusedpadding || "8px",
          paddingBottom:
            section.focusedpaddingBottom || section.focusedpadding || "8px",
          paddingLeft: section.focusedpaddingLeft || section.focusedpadding || "8px",
          margin: section.focusedmargin || section.margin || "0px",
          marginTop: section.focusedmarginTop || section.focusedmargin || "0px",
          marginRight: section.focusedmarginRight || section.focusedmargin || "0px",
          marginBottom: section.focusedmarginBottom || section.focusedmargin || "0px",
          marginLeft: section.focusedmarginLeft || section.focusedmargin || "0px",

          borderTop:
            section.focusedborderTop ||
            section.focusedborder ||
            section.border ||
            "1px solid black",
          borderRight:
            section.focusedborderRight ||
            section.focusedborder ||
            section.border ||
            "1px solid black",
          borderBottom:
            section.focusedborderBottom ||
            section.focusedborder ||
            section.border ||
            "1px solid black",
          borderLeft:
            section.focusedborderLeft ||
            section.focusedborder ||
            section.border ||
            "1px solid black",
          borderSapcing: section.focusedborderSpacing || section.borderSpacing || "0px",
          borderCollapse:
            section.focusedborderCollapse || section.borderCollapse || "0px",
          borderRadius: section.focusedborderRadius || section.borderRadius || "0",
          outline: section.focusedoutline || section.outline || "none",

          position: section.focusedposition || section.position || "relative",
          top: section.focusedtop || section.top || "auto",
          right: section.focusedright || section.right || "auto",
          bottom: section.focusedbottom || section.bottom || "auto",
          left: section.focusedleft || section.left || "auto",
          zIndex: section.focusedzIndex || section.zIndex || "auto",
          boxShadow: section.focusedboxShadow || section.boxShadow || "none",
          opacity: section.focusedOpacity || section.opacity || 1,
          display: section.focuseddisplay || section.display || "block",
          flexDirection: section.focusedflexDirection || section.flexDirection || "column",
          flexWrap: section.focusedflexWrap || "nowrap",
          order: section.focusedorder || "0",
          flexGrow: section.focusedflexGrow || "0",
          flexShrink: section.focusedflexShrink || "1",
          flexBasis: section.focusedflexBasis || "auto",

          gridTemplateColumns: section.focusedgridTemplateColumns || "none",
          gridTemplateRows: section.focusedgridTemplateRows || "none",
          gridArea: section.focusedgridArea || "auto",
          gridAutoFlow: section.focusedgridAutoFlow || "row",
          gridAutoColumns: section.focusedgridAutoColumns || "auto",
          gridAutoRows: section.focusedgridAutoRows || "auto",
          columnGap: section.focusedcolumnGap || "0",
          rowGap: section.focusedrowGap || "0",

          justifyContent: section.focusedjustifyContent || "flex-start",
          alignItems: section.focusedalignItems || "stretch",
          alignContent: section.focusedalignContent || "stretch",
          alignSelf: section.focusedalignSelf || "auto",
          // placeContent: section.focusedplaceContent || "normal",
          // placeItems: section.focusedplaceItems || "normal",
          justifyItems: section.focusedjustifyItems || "stretch",
          justifySelf: section.focusedjustifySelf || "auto",

          gap: section.focusedgap || "0",
          visibility: section.focusedvisibility || "visible",

          color: section.focusedcolor || section.color || "#000000",
          backgroundColor:
            section.focusedbackgroundColor || section.backgroundColor || "#ffffff",
          backgroundImage: (() => {
            if (section.focusedbackgroundImage) {
              if (section.focusedbackgroundImage.startsWith("./")) {
                const imageName = section.focusedbackgroundImage.replace(
                  "./",
                  ""
                );
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${section.focusedbackgroundImage})`;
            }
            if (section.backgroundImage) {
              if (section.backgroundImage.startsWith("./")) {
                const imageName = section.backgroundImage.replace("./", "");
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${section.backgroundImage})`;
            }
            return (
              constructFocusedGradientString() ||
              constructGradientString() ||
              "none"
            );
          })(),
          backgroundPosition:
            section.focusedbackgroundPosition || section.backgroundPosition || "0% 0%",
          backgroundSize:
            section.focusedbackgroundSize || section.backgroundSize || "auto",
          backgroundRepeat:
            section.focusedbackgroundRepeat || section.backgroundRepeat || "repeat",
          backgroundAttachment:
            section.focusedbackgroundAttachment ||
            section.backgroundAttachment ||
            "scroll",

          fontFamily: section.focusedfontFamily || section.fontFamily || "inherit",
          fontSize: section.focusedfontSize || section.fontSize || "inherit",
          fontWeight: section.focusedfontWeight || section.fontWeight || "normal",
          fontStyle: section.focusedfontStyle || section.fontStyle || "normal",
          fontVariant: section.focusedfontVariant || section.fontVariant || "normal",
          lineHeight: section.focusedlineHeight || section.lineHeight || "normal",
          textShadow: section.focusedtextShadow || section.textShadow || "none",
          textAlign: section.focusedtextAlign || section.textAlign || "left",
          verticalAlign:
            section.focusedverticalAlign || section.verticalAlign || "baseline",
          textDecoration:
            section.focusedtextDecoration || section.textDecoration || "none",
          textTransform:
            section.focusedtextTransform || section.textTransform || "none",
          letterSpacing:
            section.focusedletterSpacing || section.letterSpacing || "normal",
          wordSpacing: section.focusedwordSpacing || section.wordSpacing || "normal",
          textIndent: section.focusedtextIndent || section.textIndent || "0",
          whiteSpace: section.focusedwhiteSpace || section.whiteSpace || "normal",
          textOverflow: section.focusedtextOverflow || section.textOverflow || "clip",
          direction: section.focuseddirection || section.direction || "ltr",
          unicodeBidi: section.focusedunicodeBidi || section.unicodeBidi || "normal",
          writingMode:
            section.focusedwritingMode || section.writingMode || "horizontal-tb",

          cursor: section.focusedcursor || section.cursor || "default",
          filter: section.focusedfilter || section.filter || "none",
          backdropFilter:
            section.focusedbackdropFilter || section.backdropFilter || "none",
          mixBlendMode: section.focusedmixBlendMode || section.mixBlendMode || "normal",
          aspectRatio: section.focusedaspectRatio || section.aspectRatio || "auto",

          transform: section.focusedtransform || section.transform || "none",
          transition: section.focusedtransition || section.transition || "none",
        }
      : {};

  const hoverStyles =
    isSectionHovered && section.pseudoClass === "hover"
      ? {
          width: section.hoveredwidth || section.width || "100%",
          height: section.hoveredheight || section.height || "auto",

          padding: section.hoveredpadding || section.padding || "8px",
          paddingTop: section.hoveredpaddingTop || section.hoveredpadding || "8px",
          paddingRight: section.hoveredpaddingRight || section.hoveredpadding || "8px",
          paddingBottom:
            section.hoveredpaddingBottom || section.hoveredpadding || "8px",
          paddingLeft: section.hoveredpaddingLeft || section.hoveredpadding || "8px",
          margin: section.hoveredmargin || section.margin || "0px",
          marginTop: section.hoveredmarginTop || section.hoveredmargin || "0px",
          marginRight: section.hoveredmarginRight || section.hoveredmargin || "0px",
          marginBottom: section.hoveredmarginBottom || section.hoveredmargin || "0px",
          marginLeft: section.hoveredmarginLeft || section.hoveredmargin || "0px",

          borderTop:
            section.hoveredborderTop ||
            section.hoveredborder ||
            section.border ||
            "1px solid black",
          borderRight:
            section.hoveredborderRight ||
            section.hoveredborder ||
            section.border ||
            "1px solid black",
          borderBottom:
            section.hoveredborderBottom ||
            section.hoveredborder ||
            section.border ||
            "1px solid black",
          borderLeft:
            section.hoveredborderLeft ||
            section.hoveredborder ||
            section.border ||
            "1px solid black",
          borderSapcing: section.hoveredborderSpacing || section.borderSpacing || "0px",
          borderCollapse:
            section.hoveredborderCollapse || section.borderCollapse || "0px",
          borderRadius: section.hoveredborderRadius || section.borderRadius || "0",
          outline: section.hoveredoutline || section.outline || "none",

          position: section.hoveredposition || section.position || "relative",
          top: section.hoveredtop || section.top || "auto",
          right: section.hoveredright || section.right || "auto",
          bottom: section.hoveredbottom || section.bottom || "auto",
          left: section.hoveredleft || section.left || "auto",
          zIndex: section.hoveredzIndex || section.zIndex || "auto",
          boxShadow: section.hoveredboxShadow || section.boxShadow || "none",
          opacity: section.hoveredOpacity || section.opacity || 1,
          display: section.hovereddisplay || section.display || "block",
          flexDirection: section.hoveredflexDirection || section.flexDirection || "column",
          flexWrap: section.hoveredflexWrap || "nowrap",
          order: section.hoveredorder || "0",
          flexGrow: section.hoveredflexGrow || "0",
          flexShrink: section.hoveredflexShrink || "1",
          flexBasis: section.hoveredflexBasis || "auto",

          gridTemplateColumns: section.hoveredgridTemplateColumns || "none",
          gridTemplateRows: section.hoveredgridTemplateRows || "none",
          gridArea: section.hoveredgridArea || "auto",
          gridAutoFlow: section.hoveredgridAutoFlow || "row",
          gridAutoColumns: section.hoveredgridAutoColumns || "auto",
          gridAutoRows: section.hoveredgridAutoRows || "auto",
          columnGap: section.hoveredcolumnGap || "0",
          rowGap: section.hoveredrowGap || "0",

          justifyContent: section.hoveredjustifyContent || "flex-start",
          alignItems: section.hoveredalignItems || "stretch",
          alignContent: section.hoveredalignContent || "stretch",
          alignSelf: section.hoveredalignSelf || "auto",
          // placeContent: section.hoveredplaceContent || "normal",
          // placeItems: section.hoveredplaceItems || "normal",
          justifyItems: section.hoveredjustifyItems || "stretch",
          justifySelf: section.hoveredjustifySelf || "auto",

          gap: section.hoveredgap || "0",
          visibility: section.hoveredvisibility || "visible",

          color: section.hoveredcolor || section.color || "#000000",
          backgroundColor:
            section.hoveredbackgroundColor || section.backgroundColor || "#ffffff",
          backgroundImage: (() => {
            if (section.hoveredbackgroundImage) {
              if (section.hoveredbackgroundImage.startsWith("./")) {
                const imageName = section.hoveredbackgroundImage.replace("./", "");
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${section.hoveredbackgroundImage})`;
            }
            if (section.backgroundImage) {
              if (section.backgroundImage.startsWith("./")) {
                const imageName = section.backgroundImage.replace("./", "");
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${section.backgroundImage})`;
            }
            return (
              constructHoverGradientString() ||
              constructGradientString() ||
              "none"
            );
          })(),
          backgroundPosition:
            section.hoveredbackgroundPosition || section.backgroundPosition || "0% 0%",
          backgroundSize:
            section.hoveredbackgroundSize || section.backgroundSize || "auto",
          backgroundRepeat:
            section.hoveredbackgroundRepeat || section.backgroundRepeat || "repeat",
          backgroundAttachment:
            section.hoveredbackgroundAttachment ||
            section.backgroundAttachment ||
            "scroll",

          fontFamily: section.hoveredfontFamily || section.fontFamily || "inherit",
          fontSize: section.hoveredfontSize || section.fontSize || "inherit",
          fontWeight: section.hoveredfontWeight || section.fontWeight || "normal",
          fontStyle: section.hoveredfontStyle || section.fontStyle || "normal",
          fontVariant: section.hoveredfontVariant || section.fontVariant || "normal",
          lineHeight: section.hoveredlineHeight || section.lineHeight || "normal",
          textShadow: section.hoveredtextShadow || section.textShadow || "none",
          textAlign: section.hoveredtextAlign || section.textAlign || "left",
          verticalAlign:
            section.hoveredverticalAlign || section.verticalAlign || "baseline",
          textDecoration:
            section.hoveredtextDecoration || section.textDecoration || "none",
          textTransform:
            section.hoveredtextTransform || section.textTransform || "none",
          letterSpacing:
            section.hoveredletterSpacing || section.letterSpacing || "normal",
          wordSpacing: section.hoveredwordSpacing || section.wordSpacing || "normal",
          textIndent: section.hoveredtextIndent || section.textIndent || "0",
          whiteSpace: section.hoveredwhiteSpace || section.whiteSpace || "normal",
          textOverflow: section.hoveredtextOverflow || section.textOverflow || "clip",
          direction: section.hovereddirection || section.direction || "ltr",
          unicodeBidi: section.hoveredunicodeBidi || section.unicodeBidi || "normal",
          writingMode:
            section.hoveredwritingMode || section.writingMode || "horizontal-tb",

          cursor: section.hoveredcursor || section.cursor || "default",
          filter: section.hoveredfilter || section.filter || "none",
          backdropFilter:
            section.hoveredbackdropFilter || section.backdropFilter || "none",
          mixBlendMode: section.hoveredmixBlendMode || section.mixBlendMode || "normal",
          aspectRatio: section.hoveredaspectRatio || section.aspectRatio || "auto",

          transform: section.hoveredtransform || section.transform || "none",
          transition: section.hoveredtransition || section.transition || "none",
        }
      : {};

  const activeStyles =
    isSectionActive && section.pseudoClass === "active"
      ? {
          width: section.activatedwidth || section.width || "100%",
          height: section.hoveredheight || section.height || "auto",

          padding: section.activatedpadding || section.padding || "8px",
          paddingTop: section.activatedpaddingTop || section.activatedpadding || "8px",
          paddingRight:
            section.activatedpaddingRight || section.activatedpadding || "8px",
          paddingBottom:
            section.activatedpaddingBottom || section.activatedpadding || "8px",
          paddingLeft:
            section.activatedpaddingLeft || section.activatedpadding || "8px",
          margin: section.activatedmargin || section.margin || "0px",
          marginTop: section.activatedmarginTop || section.activatedmargin || "0px",
          marginRight: section.activatedmarginRight || section.activatedmargin || "0px",
          marginBottom:
            section.activatedmarginBottom || section.activatedmargin || "0px",
          marginLeft: section.activatedmarginLeft || section.activatedmargin || "0px",

          borderTop:
            section.activatedborderTop ||
            section.activatedborder ||
            section.border ||
            "1px solid black",
          borderRight:
            section.activatedborderRight ||
            section.activatedborder ||
            section.border ||
            "1px solid black",
          borderBottom:
            section.activatedborderBottom ||
            section.activatedborder ||
            section.border ||
            "1px solid black",
          borderLeft:
            section.activatedborderLeft ||
            section.activatedborder ||
            section.border ||
            "1px solid black",
          borderSapcing:
            section.activatedborderSpacing || section.borderSpacing || "0px",
          borderCollapse:
            section.activatedborderCollapse || section.borderCollapse || "0px",
          borderRadius: section.activatedborderRadius || section.borderRadius || "0",
          outline: section.activatedoutline || section.outline || "none",

          position: section.activatedposition || section.position || "relative",
          top: section.activatedtop || section.top || "auto",
          right: section.activatedright || section.right || "auto",
          bottom: section.activatedbottom || section.bottom || "auto",
          left: section.activatedleft || section.left || "auto",
          zIndex: section.activatedzIndex || section.zIndex || "auto",

          boxShadow: section.activatedboxShadow || section.boxShadow || "none",
          opacity: section.activatedOpacity || section.opacity || 1,
          display: section.activateddisplay || section.display || "block",
          flexDirection: section.activatedflexDirection ||  section.flexDirection || "column",
          flexWrap: section.activatedflexWrap || "nowrap",
          order: section.activatedorder || "0",
          flexGrow: section.activatedflexGrow || "0",
          flexShrink: section.activatedflexShrink || "1",
          flexBasis: section.activatedflexBasis || "auto",

          gridTemplateColumns: section.activatedgridTemplateColumns || "none",
          gridTemplateRows: section.activatedgridTemplateRows || "none",
          gridArea: section.activatedgridArea || "auto",
          gridAutoFlow: section.activatedgridAutoFlow || "row",
          gridAutoColumns: section.activatedgridAutoColumns || "auto",
          gridAutoRows: section.activatedgridAutoRows || "auto",
          columnGap: section.activatedcolumnGap || "0",
          rowGap: section.activatedrowGap || "0",

          justifyContent: section.activatedjustifyContent || "flex-start",
          alignItems: section.activatedalignItems || "stretch",
          alignContent: section.activatedalignContent || "stretch",
          alignSelf: section.activatedalignSelf || "auto",
          // placeContent: section.activatedplaceContent || "normal",
          // placeItems: section.activatedplaceItems || "normal",
          justifyItems: section.activatedjustifyItems || "stretch",
          justifySelf: section.activatedjustifySelf || "auto",

          gap: section.activatedgap || "0",
          visibility: section.activatedvisibility || "visible",

          color: section.activatedcolor || section.color || "#000000",
          backgroundColor:
            section.activatedbackgroundColor || section.backgroundColor || "#ffffff",
          backgroundImage: (() => {
            if (section.activatedbackgroundImage) {
              if (section.activatedbackgroundImage.startsWith("./")) {
                const imageName = section.activatedbackgroundImage.replace(
                  "./",
                  ""
                );
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${section.activatedbackgroundImage})`;
            }
            if (section.backgroundImage) {
              if (section.backgroundImage.startsWith("./")) {
                const imageName = section.backgroundImage.replace("./", "");
                const file = files.find((f) => f.name === imageName);
                return file && file.data ? `url(${file.data})` : "none";
              }
              return `url(${section.backgroundImage})`;
            }
            return (
              constructActivatedGradientString() ||
              constructGradientString() ||
              "none"
            );
          })(),
          backgroundPosition:
            section.activatedbackgroundPosition ||
            section.backgroundPosition ||
            "0% 0%",
          backgroundSize:
            section.activatedbackgroundSize || section.backgroundSize || "auto",
          backgroundRepeat:
            section.activatedbackgroundRepeat || section.backgroundRepeat || "repeat",
          backgroundAttachment:
            section.activatedbackgroundAttachment ||
            section.backgroundAttachment ||
            "scroll",

          fontFamily: section.activatedfontFamily || section.fontFamily || "inherit",
          fontSize: section.activatedfontSize || section.fontSize || "inherit",
          fontWeight: section.activatedfontWeight || section.fontWeight || "normal",
          fontStyle: section.activatedfontStyle || section.fontStyle || "normal",
          fontVariant: section.activatedfontVariant || section.fontVariant || "normal",
          lineHeight: section.activatedlineHeight || section.lineHeight || "normal",
          textShadow: section.activatedtextShadow || section.textShadow || "none",
          textAlign: section.activatedtextAlign || section.textAlign || "left",
          verticalAlign:
            section.activatedverticalAlign || section.verticalAlign || "baseline",
          textDecoration:
            section.activatedtextDecoration || section.textDecoration || "none",
          textTransform:
            section.activatedtextTransform || section.textTransform || "none",
          letterSpacing:
            section.activatedletterSpacing || section.letterSpacing || "normal",
          wordSpacing: section.activatedwordSpacing || section.wordSpacing || "normal",
          textIndent: section.activatedtextIndent || section.textIndent || "0",
          whiteSpace: section.activatedwhiteSpace || section.whiteSpace || "normal",
          textOverflow: section.activatedtextOverflow || section.textOverflow || "clip",
          direction: section.activateddirection || section.direction || "ltr",
          unicodeBidi: section.activatedunicodeBidi || section.unicodeBidi || "normal",
          writingMode:
            section.activatedwritingMode || section.writingMode || "horizontal-tb",

          cursor: section.activatedcursor || section.cursor || "default",
          filter: section.activatedfilter || section.filter || "none",
          backdropFilter:
            section.activatedbackdropFilter || section.backdropFilter || "none",
          mixBlendMode:
            section.activatedmixBlendMode || section.mixBlendMode || "normal",
          aspectRatio: section.activatedaspectRatio || section.aspectRatio || "auto",

          transform: section.activatedtransform || section.transform || "none",
          transition: section.activatedtransition || section.transition || "none",
        }
      : {};

  const sectionStyle = {
    width: section.width || "100%",
    height: section.height || "auto",
    padding: section.padding || "8px",
    paddingTop: section.paddingTop || section.padding || "8px",
    paddingRight: section.paddingRight || section.padding || "8px",
    paddingBottom: section.paddingBottom || section.padding || "8px",
    paddingLeft: section.paddingLeft || section.padding || "8px",
    margin: section.margin || "0px",
    marginTop: section.marginTop || section.margin || "0px",
    marginRight: section.marginRight || section.margin || "0px",
    marginBottom: section.marginBottom || section.margin || "0px",
    marginLeft: section.marginLeft || section.margin || "0px",

    borderTop: section.borderTop || section.border || "1px solid #000000",
    borderRight: section.borderRight || section.border || "1px solid #000000",
    borderBottom: section.borderBottom || section.border || "1px solid #000000",
    borderLeft: section.borderLeft || section.border || "1px solid #000000",

    borderSapcing: section.borderSpacing || "0px",
    borderCollapse: section.borderCollapse || "0px",
    borderRadius: section.borderRadius || "0",
    boxSizing: section.boxSizing || "border-box",

    position: section.position || "relative",
    top: section.top || "auto",
    right: section.right || "auto",
    bottom: section.bottom || "auto",
    left: section.left || "auto",
    zIndex: section.zIndex || "auto",

    display: section.display || "block",
    flexDirection: section.flexDirection || "column",
    flexWrap: section.flexWrap || "nowrap",
    order: section.order || "0",
    flexGrow: section.flexGrow || "0",
    flexShrink: section.flexShrink || "1",
    flexBasis: section.flexBasis || "auto",

    gridTemplateColumns: section.gridTemplateColumns || "none",
    gridTemplateRows: section.gridTemplateRows || "none",
    gridArea: section.gridArea || "auto",
    gridAutoFlow: section.gridAutoFlow || "row",
    gridAutoColumns: section.gridAutoColumns || "auto",
    gridAutoRows: section.gridAutoRows || "auto",
    columnGap: section.columnGap || "0",
    rowGap: section.rowGap || "0",

    justifyContent: section.justifyContent || "flex-start",
    alignItems: section.alignItems || "stretch",
    alignContent: section.alignContent || "stretch",
    alignSelf: section.alignSelf || "auto",
    // placeContent: section.placeContent || "normal",
    // placeItems: section.placeItems || "normal",
    justifyItems: section.justifyItems || "stretch",
    justifySelf: section.justifySelf || "auto",

    gap: section.gap || "0",
    visibility: section.visibility || "visible",

    color: section.color || "#000000",
    backgroundImage: backgroundImageUrl
      ? `url(${backgroundImageUrl})`
      : constructGradientString(),

    backgroundPosition: section.backgroundPosition || "0% 0%",
    backgroundSize: section.backgroundSize || "auto",
    backgroundRepeat: section.backgroundRepeat || "repeat",
    backgroundAttachment: section.backgroundAttachment || "scroll",
    boxShadow: section.boxShadow || "none",
    filter: section.filter || "none",
    backdropFilter: section.backdropFilter || "none",
    mixBlendMode: section.mixBlendMode || "normal",
    aspectRatio: section.aspectRatio || "auto",

    resize: section.resize || "none",
    userSelect: section.userSelect || "auto",
    overflow: section.resize && section.resize !== "none" ? "auto" : "visible",
    overflowX: section.overflowX || "hidden",
    overflowY: section.overflowY || "hidden",

    fontSize: section.fontSize || "inherit",
    fontFamily: section.fontFamily || "inherit",
    fontWeight: section.fontWeight || "normal",
    fontStyle: section.fontStyle || "normal",
    fontVariant: section.fontVariant || "normal",
    lineHeight: section.lineHeight || "normal",
    textAlign: section.textAlign || "left",
    textDecoration: section.textDecoration || "none",
    textTransform: section.textTransform || "none",
    textShadow: section.textShadow || "none",
    verticalAlign: section.verticalAlign || "baseline",
    letterSpacing: section.letterSpacing || "normal",
    wordSpacing: section.wordSpacing || "normal",
    textIndent: section.textIndent || "0",
    whiteSpace: section.whiteSpace || "normal",
    textOverflow: section.textOverflow || "clip",
    direction: section.direction || "ltr",
    unicodeBidi: section.unicodeBidi || "normal",
    writingMode: section.writingMode || "horizontal-tb",
    cursor: section.cursor || "default",
    outline: section.outline || "none",

    backgroundColor: isHovered ? "#ADD8E6" : section.backgroundColor || "#ffffff",

    listStyleType: section.listStyleType || "disc",
    listStyleImage: section.listStyleImage ? `url(${section.listStyleImage})` : "none",
    listStylePosition: section.listStylePosition || "outside",

    transform: section.transform || "none",
    transition:
      section.transition || "transform 0.3s ease, background-color 0.3s ease",

    ...hoverStyles,
    ...focusedStyles,
    ...activeStyles,
  };
  const combinedRef = useMemo(
    () => (node) => {
      sectionRef.current = node;
      drop(node);
    },
    [drop]
  );

  return (
    <header
      ref={combinedRef}
      style={sectionStyle}
      onMouseEnter={() => setIsSectionHovered(true)}
      onMouseLeave={() => setIsSectionHovered(false)}
      onFocus={() => setIsSectionFocused(true)}
      onBlur={() => setIsSectionFocused(false)}
      onMouseDown={() => setIsSectionActive(true)}
      onMouseUp={() => setIsSectionActive(false)}
      tabIndex={0}
    >
      {section.children &&
        section.children.map((child) => {
          switch (child.name) {
            case "Button":
              return <ButtonComponent key={child.id} button={child} />;
            case "Image":
              return <ImageComponent key={child.id} image={child} />;
            case "Heading":
              return <HeaderComponent key={child.id} header={child} />;
            case "Paragraph":
              return <ParagraphComponent key={child.id} paragraph={child} />;
            case "Icon":
              return <IconComponent key={child.id} icon={child} />;
            case "Link":
              return <LinkComponent key={child.id} link={child} />;
            case "Checkbox":
              return <CheckboxComponent key={child.id} checkbox={child} />;
            case "Radio":
              return <RadioComponent key={child.id} radio={child} />;
            case "List":
              return <ListComponent key={child.id} list={child} />;
            case "Table":
              return <TableComponent key={child.id} table={child} />;
            case "Video":
              return <VideoComponent key={child.id} video={child} />;
            case "Text":
              return <TextInputComponent key={child.id} textInput={child} />;
            case "Range":
              return <RangeComponent key={child.id} range={child} />;
            case "Password":
              return (
                <PasswordInputComponent key={child.id} passwordInput={child} />
              );
            case "Email":
              return <EmailInputComponent key={child.id} emailInput={child} />;
            case "Number":
              return (
                <NumberInputComponent key={child.id} numberInput={child} />
              );
            case "Date":
              return <DateInputComponent key={child.id} dateInput={child} />;
            case "Color":
              return <ColorInputComponent key={child.id} colorInput={child} />;
            case "File":
              return <FileInputComponent key={child.id} fileInput={child} />;
            case "Audio":
              return <AudioComponent key={child.id} audio={child} />;
            case "DropDown":
              return <DropDownComponent key={child.id} dropdown={child} />;
            case "Output":
              return <OutputComponent key={child.id} output={child} />;
            case "Map":
              return <MapComponent key={child.id} map={child} />;
            case "Nav":
              return <Nav key={child.id} nav={child} />;
            case "Header":
              return <HeaderTag key={child.id} headertag={child} />;
            case "HGroup":
              return <Hgroup key={child.id} hgroup={child} />;
            case "Footer":
              return <Footer key={child.id} footer={child} />;
            case "Section":
              return <Section key={child.id} section={child} />;
            case "Span":
              return <Span key={child.id} span={child} />;
            case "Form":
              return <FormComponent key={child.id} form={child} />;
            case "Main":
              return <MainComponent key={child.id} main={child} />;
            case "Article":
              return <ArticleComponent key={child.id} article={child} />;
            case "Aside":
              return <AsideComponent key={child.id} aside={child} />;
            case "HorizontalRule":
              return <HRTag key={child.id} div={child} />;
            case "LineBreak":
              return <BRTag key={child.id} div={child} />;
            case "Iframe":
              return <IframeComponent key={child.id} div={child} />;
            default:
              return <Division key={child.id} div={child} />;
          }
        })}
    </header>
  );
};

export default Section;
